import { useState, useEffect, useCallback } from "react";
import {
  FaTimes,
  FaChevronLeft,
  FaChevronRight,
  FaInstagram,
} from "react-icons/fa";

export default function Gallery() {
  // -------------------- STATE --------------------
  const [selected, setSelected] = useState(null);
  const [index, setIndex] = useState(0);
  const [loaded, setLoaded] = useState(new Set());

  const images = [
    "/images/dania/anatomia_2.jpg",
    "/images/dania/anatomia_3.jpg",
    "/images/dania/anatomia_4.jpg",
    "/images/dania/anatomia_5.jpg",
    "/images/dania/anatomia_bmw.jpg",
    "/images/dania/anatomia_6.jpg",
    "/images/dania/anatomia_7.jpg",
    "/images/dania/anatomia_8.jpg",
    "/images/dania/anatomia_9.jpg",
    "/images/dania/anatomia_10.jpg",
    "/images/dania/anatomia_11.jpg",
    "/images/dania/anatomia_12.jpg",
  ];

  // -------------------- HANDLERS --------------------
  const openModal = (src, i) => {
    setSelected(src);
    setIndex(i);
    document.body.style.overflow = "hidden";
  };
  const closeModal = () => {
    setSelected(null);
    document.body.style.overflow = "unset";
  };

  const navigate = useCallback(
    (dir) => {
      const next =
        dir === "next"
          ? (index + 1) % images.length
          : (index - 1 + images.length) % images.length;
      setIndex(next);
      setSelected(images[next]);
    },
    [index, images]
  );

  const handleLoad = (idx) => setLoaded((prev) => new Set(prev).add(idx));

  // -------------------- KEYBOARD --------------------
  useEffect(() => {
    const onKey = (e) => {
      if (!selected) return;
      if (e.key === "Escape") closeModal();
      if (e.key === "ArrowRight") navigate("next");
      if (e.key === "ArrowLeft") navigate("prev");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selected, navigate]);

  return (
    <section
      id="gallery"
      className="relative py-20 overflow-hidden bg-gradient-to-b from-white via-cream-50 to-white"
    >
      {/* ====== NAGŁÓWEK ====== */}
      <div className="relative z-10 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <header className="text-center mb-16">
          <h2 className="text-5xl md:text-6xl lg:text-7xl font-bold text-charcoal-800 mb-6">
            Galeria
            <span className="block text-3xl md:text-4xl font-light text-cream-700 mt-2">
              smaków i chwil
            </span>
          </h2>
          <div className="w-24 h-1 bg-cream-600 mx-auto mb-6" />
          <p className="text-xl text-charcoal-600 max-w-2xl mx-auto">
            Każde danie to historia, każdy event to niezapomniane wspomnienie
          </p>
        </header>

        <div className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-4 space-y-4">
          {images.map((src, i) => (
            <figure
              key={src}
              className={`relative break-inside-avoid group cursor-pointer
                ${
                  i % 3 === 0
                    ? "aspect-square"
                    : i % 3 === 1
                    ? "aspect-[4/5]"
                    : "aspect-[5/4]"
                }`}
              onClick={() => openModal(src, i)}
            >
              {!loaded.has(i) && (
                <div className="absolute inset-0 bg-cream-200 rounded-2xl animate-pulse" />
              )}

              <img
                src={src}
                alt={`Zdjęcie ${i + 1}`}
                onLoad={() => handleLoad(i)}
                loading="lazy"
                className={`object-cover w-full h-full rounded-2xl shadow-lg transition-transform duration-500
                  ${loaded.has(i) ? "opacity-100" : "opacity-0"}
                  group-hover:scale-105`}
              />

              <figcaption className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl" />
            </figure>
          ))}
        </div>
      </div>

      {/* ====== MODAL ====== */}
      {selected && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90"
          onClick={closeModal}
        >
          {/* close */}
          <button
            aria-label="Zamknij"
            className="absolute top-4 right-4 text-white/70 hover:text-white p-2"
            onClick={closeModal}
          >
            <FaTimes className="w-8 h-8" />
          </button>

          {/* nav */}
          <button
            aria-label="Poprzednie"
            className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 text-white/70 hover:text-white p-2"
            onClick={(e) => {
              e.stopPropagation();
              navigate("prev");
            }}
          >
            <FaChevronLeft className="w-8 h-8" />
          </button>
          <button
            aria-label="Następne"
            className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 text-white/70 hover:text-white p-2"
            onClick={(e) => {
              e.stopPropagation();
              navigate("next");
            }}
          >
            <FaChevronRight className="w-8 h-8" />
          </button>

          {/* image */}
          <div
            className="relative max-w-[90vw] max-h-[90vh]"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={selected}
              alt="Powiększone zdjęcie"
              className="max-w-full max-h-[90vh] object-contain"
            />
            <span className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/50 text-white px-4 py-1 rounded-full text-sm">
              {index + 1} / {images.length}
            </span>
          </div>
        </div>
      )}
    </section>
  );
}
